/** Public welcome page at / (ALP-85). Copy follows the product handbook (docs/product/01-business/overview.md). */
export const welcome = {
  meta: { title: "Malfak" },
  skip: "Skip to content",
  nav: {
    label: "Sections",
    features: "Features",
    how: "How it works",
    plans: "Plans",
    faq: "FAQ",
    login: "Log in",
    signup: "Sign up",
    menu: "Open menu",
    menuTitle: "Menu",
    language: "العربية",
    themeLight: "Switch to light theme",
    themeDark: "Switch to dark theme",
  },
  hero: {
    eyebrow: "AI social media management · Instagram",
    title: "Every comment and DM answered,",
    titleAccent: "in your brand's voice.",
    text: "Malfak brings your Instagram comments and messages into one live inbox. AI sorts them and drafts replies, your rules decide what happens, and a person approves whenever it matters. Arabic first, English too.",
    cta: "Request access",
    secondary: "Log in",
    note: "Free to request. We contact every new team personally.",
    points: { arabic: "Arabic and English, right to left done properly", approvals: "AI never acts without your rules or your approval", lost: "Nothing is lost: deleted comments stay in Malfak" },
  },
  demo: {
    label: "A live preview of the Malfak inbox: new comments arrive sorted into AI categories and a drafted reply waits for approval.",
    inbox: "Inbox",
    live: "Live",
    intents: { price: "Price question", praise: "Praise", spam: "Spam", order: "Order" },
    sentiment: { positive: "Positive", neutral: "Neutral", negative: "Negative" },
    hidden: "Hidden by rule",
    draft: "AI draft",
    approve: "Approve",
    approved: "Approved and sent",
    replies: "Replies this week",
    response: "Median first reply",
  },
  features: {
    eyebrow: "What Malfak does",
    title: "One place for your brand's social conversations",
    text: "Built for teams in the Middle East who sell and support customers in Instagram comments and DMs.",
    items: {
      inbox: { title: "Inbox and moderation", text: "Comments and DMs from every account in one list, sorted by AI into categories for intent, sentiment and toxicity. Reply, hide, tag or bulk-act in seconds." },
      agents: { title: "AI agents and auto-replies", text: "Replies drafted in your tone. Each rule runs automatically, asks for approval first, or only suggests." },
      copilot: { title: "The Assistant", text: "Ask in Arabic or English and get live charts, tables and comment lists. It proposes changes; a person confirms them." },
      dashboards: { title: "Live dashboards", text: "Editable dashboards whose widgets stay live, per account and per post, plus best times to post." },
      calendar: { title: "Calendar intelligence", text: "National, religious and international days per country, with mood and tone guidance and conflict warnings." },
      c360: { title: "Customer 360", text: "One timeline per person across comments, replies and DMs, with notes and marks such as VIP or harmful." },
      rules: { title: "Automation rules", text: "Cheap conditions first, AI only when needed: keywords, links, language, marked commenters. Simulate before you switch on." },
    },
  },
  how: {
    eyebrow: "How it works",
    title: "From a new comment to a decision, in four steps",
    steps: {
      connect: { title: "Connect Instagram", text: "Sign in with Instagram's official login. Malfak imports your posts, comments and messages." },
      triage: { title: "AI triages", text: "Every new comment and DM is labeled, prioritized and, when a rule asks, given a drafted reply." },
      approve: { title: "You approve", text: "Risky actions and AI drafts wait in Approvals. Edit, approve or reject; nothing goes out on its own." },
      insights: { title: "Insights", text: "Dashboards, analytics and the Assistant show what people ask, how fast you answer and what works." },
    },
  },
  plans: {
    eyebrow: "Plans",
    title: "A plan for every stage",
    text: "Plans are set up with you when your access is approved.",
    price: "Contact us",
    cta: "Request access",
    items: {
      trial: { name: "Trial", text: "Everything in Pro for 14 days, then Free." },
      free: { name: "Free", text: "One account, the essentials." },
      pro: { name: "Pro", text: "For growing brands: the Assistant, dashboards, bulk moderation." },
      business: { name: "Business", text: "Teams and agencies: everything, generous limits." },
    },
  },
  trust: {
    eyebrow: "Trust and security",
    title: "You stay in control",
    items: {
      approval: { title: "Human approval", text: "The AI sorts, drafts and proposes. Your rules and your team decide what is sent." },
      login: { title: "Official Instagram login", text: "Accounts connect through Instagram's own login. We never ask for or see your password." },
      audit: { title: "Everything audited", text: "Every automation run has a timeline and every staff change is written to an audit log." },
      deletion: { title: "Your data, your call", text: "Export or erase a person's data, choose retention, and request full deletion any time." },
      arabic: { title: "Arabic first", text: "Dialect-aware rules fold letter variants and Arabic digits, so one rule catches how people really type." },
    },
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions, answered",
    items: {
      what: { q: "What is Malfak?", a: "Malfak (ملفك, “your file”) is one place for a brand's social channels: a live inbox, automation with approvals, publishing, dashboards, calendar intelligence and an AI Assistant." },
      channels: { q: "Which channels are supported?", a: "Instagram professional accounts (Business or Creator) today. Facebook Pages, Messenger and WhatsApp are coming soon." },
      ai: { q: "Does the AI reply on its own?", a: "Only if you write a rule that allows it. Each rule runs automatically, waits for approval, or only suggests, and you can always put a person in the loop." },
      arabic: { q: "Does it work in Arabic?", a: "Yes. The whole app works in Arabic and English, right to left and left to right, in light and dark themes." },
      access: { q: "How do I get access?", a: "Press Sign up and tell us about your business and why you want to use Malfak. We review every request and contact you." },
      pricing: { q: "How much does it cost?", a: "Contact us. We set up the plan that fits your team when we approve your request; you can start on a Trial." },
      deletion: { q: "Can I delete my data?", a: "Yes. You can erase a person's data from their profile, and request deletion of everything from the data deletion page." },
    },
  },
  cta: { title: "Ready to answer every customer, faster?", text: "Tell us about your brand. We will get back to you.", button: "Request access" },
  footer: {
    tagline: "AI social media management, Arabic first.",
    product: "Product",
    legal: "Legal",
    account: "Account",
    privacy: "Privacy",
    terms: "Terms",
    deletion: "Data deletion",
    contact: "Questions? Request access and tell us what you need; we reply to every request.",
    founder: "Founder",
    linkedinCompany: "Malfak on LinkedIn",
    linkedinFounder: "Founder on LinkedIn",
  },
};
